import React, { useContext, useEffect, useRef, useState } from 'react';
import AuthUserContext from '../../components/Session/context';
import FormNavConfirm from '../../components/form/FormNavConfirm';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';

import { RoundedInput } from '../../components/form';

import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faFileImage } from '@fortawesome/free-solid-svg-icons';
import { openConfirmDialog } from '../../components/Dialog';
import { openSnackbar } from '../../components/Notifier';

import { initField } from '../../utils/utilities';
import Utilities from '../../utils/utilities';

import { set, get, startCase, cloneDeep } from 'lodash';
import { FreightForwarder, Image, ModeCode } from '../../../types';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      display: 'flex',
      flexWrap: 'wrap',
      padding: '12px',
    },
    paper: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.spacing(1),
    },
    leftIcon: {
      marginRight: theme.spacing(1),
    },
    fullHeight: {
      height: '100%',
    },
    roundedButton: {
      borderRadius: '100px',
      marginRight: theme.spacing(2.5),
    },
    deleteButton: {
      backgroundColor: (theme.palette.common as any).danger,
    },
    divider: {
      width: `calc(100% + ${theme.spacing(1)}px)`,
      marginLeft: `-${theme.spacing(1) / 2}px`,
      marginBottom: theme.spacing(2),
    },
    bottomMargin: {
      marginBottom: theme.spacing(4),
    },
    avatar: {
      maxWidth: '100%',
      maxHeight: '100%',
    },
    marginYZero: {
      marginTop: '0px;',
      marginBottom: '0px;',
    },
  })
);

const maxFileMb = 5;
const FreightForwarderForm = (props: {
  submitButtonText: string;
  freightForwarder: Partial<FreightForwarder>;
  onDelete?: (freightForwarder: Partial<FreightForwarder>) => void;
  onSubmit: (freightForwarder: Partial<FreightForwarder>) => void;
}) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    authUser: {
      constants: { modes },
    },
  } = useContext(AuthUserContext);
  const [pristineFreightForwarder, setPristineFreightForwarder] = useState(cloneDeep(props.freightForwarder));
  const [freightForwarder, setFreightForwarder] = useState(cloneDeep(props.freightForwarder));
  const [previewUrl, setPreviewUrl] = useState(
    Utilities.getPhotoUrl(props.freightForwarder.logo_images as Image[], 'logo')
  );
  const textFields = useRef([
    { name: 'name', required: true },
    { name: 'phone_number', required: true },
    { name: 'url', required: false },
    { name: 'address', required: true },
    { name: 'suburb', required: true },
    { name: 'city', required: true },
    { name: 'region', required: true, displayName: 'State' },
    { name: 'postCode', required: true },
    { name: 'country', required: true },
    { name: 'cyberFreight.postUrl', required: false, displayName: 'TradeWindow Freight Post Url' },
    { name: 'cargoWise.postUrl', required: false },
    { name: 'postal_address', required: false },
    { name: 'primaryColor', required: true, displayName: 'Background Color Hex' },
    { name: 'baseCurrencyCode', required: true },
  ]).current;

  useEffect(() => {
    let _freightForwarder = { ...props.freightForwarder };
    _freightForwarder.client_tiers = _freightForwarder.client_tiers || [];
    textFields.forEach((textField) => {
      initField(_freightForwarder, textField.name, '');
    });
    initField(_freightForwarder, 'conditions_of_trade', '');
    let modeCodes = modes.map((m) => m.code);
    initField(_freightForwarder, 'terms_and_conditions', []);
    initField(_freightForwarder, 'volumeRoundingDp', 2);
    _freightForwarder.terms_and_conditions = _freightForwarder.terms_and_conditions?.filter((t) =>
      modeCodes.includes(t.mode as ModeCode)
    ); // only allowed modes
    modeCodes.forEach((code) => {
      if (
        _freightForwarder.terms_and_conditions &&
        _freightForwarder.terms_and_conditions?.find((t) => t.mode === code) === undefined
      ) {
        _freightForwarder.terms_and_conditions.push({
          mode: code,
          text: '',
        }); //init if not exist
      }
    });
    setFreightForwarder(_freightForwarder);
    setPristineFreightForwarder(cloneDeep(_freightForwarder));
  }, [props.freightForwarder, modes, textFields]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    setFreightForwarder((_freightForwarder) => {
      set(_freightForwarder, name, value);
      return { ..._freightForwarder };
    });
  };

  const handleFileInputChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.files && target.files.length > 0) {
      let sizeMb = (target.files[0].size / (1024 * 1024)).toFixed(2);
      if (sizeMb > maxFileMb.toFixed(2)) {
        openSnackbar({
          message: `File cannot be larger than: ${maxFileMb}MB. Current file is ${sizeMb}MB`,
          variant: 'error',
        });
      } else {
        setFreightForwarder({ ...freightForwarder, logo_images: target.files[0] });
        setPreviewUrl(URL.createObjectURL(target.files[0]));
      }
    }
  };

  const addTier = () => {
    setFreightForwarder({
      ...freightForwarder,
      client_tiers: [...(freightForwarder.client_tiers || []), { name: '' }],
    });
  };

  const deleteTier = (index: number) => {
    setFreightForwarder((_freightForwarder) => {
      _freightForwarder.client_tiers = _freightForwarder.client_tiers || [];
      _freightForwarder.client_tiers.splice(index, 1);
      return { ..._freightForwarder };
    });
  };

  const handleDelete = () => {
    openConfirmDialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete Freight Forwarder: ${freightForwarder.name}?`,
    }).then((res: any) => {
      if (res === 'OK') {
        if (props.onDelete) {
          props.onDelete(freightForwarder);
        }
      }
    });
  };

  const goBack = () => {
    setFreightForwarder(cloneDeep(freightForwarder));
    history.goBack();
  };

  const submit = () => {
    setPristineFreightForwarder(cloneDeep(freightForwarder));
    props.onSubmit(freightForwarder);
  };

  return freightForwarder ? (
    <Paper className={`${classes.paper} ${classes.root}`}>
      <FormNavConfirm formObject={freightForwarder} pristineObject={pristineFreightForwarder} />
      <Grid container spacing={3} className={classes.bottomMargin}>
        {textFields.map((textField) => {
          const displayName = textField.displayName || startCase(textField.name);
          return (
            <Grid key={textField.name} item xs={3}>
              <Typography variant="subtitle2" gutterBottom>
                {displayName}
              </Typography>
              <RoundedInput
                value={get(freightForwarder, textField.name, '')}
                name={textField.name}
                required={textField.required}
                placeholder={`Enter ${displayName}`}
                onChange={handleInputChange}
              />
            </Grid>
          );
        })}
        <Grid item xs={3}>
          <Typography variant="subtitle2" gutterBottom>
            Default Rounding Decimal Places
          </Typography>
          <RoundedInput
            value={freightForwarder.defaults?.volumeRoundingDp}
            name="defaults.volumeRoundingDp"
            required={true}
            placeholder="Round to Decimal Places"
            type="number"
            inputProps={{ min: 0 }}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} className={classes.bottomMargin}>
        <Grid item xs={3}>
          <Typography variant="subtitle2" gutterBottom>
            Logo
          </Typography>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="raised-button-file"
            type="file"
            onChange={handleFileInputChange}
          />
          <label htmlFor="raised-button-file">
            <Button variant="contained" component="span">
              {previewUrl && previewUrl.length > 0 ? (
                <img alt={freightForwarder.name} src={previewUrl} className={classes.avatar} />
              ) : (
                <FontAwesomeIcon icon={faFileImage as IconProp} size="6x" />
              )}
            </Button>
          </label>
          <Typography variant="body1">File must be an image less than {maxFileMb}MB.</Typography>
        </Grid>
      </Grid>

      <Divider className={classes.divider} />

      <Grid container spacing={3} className={classes.bottomMargin}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Client Tiers
          </Typography>
          <Button onClick={addTier} size="large" variant="contained" color="primary" className={classes.roundedButton}>
            <FontAwesomeIcon className={classes.leftIcon} icon={faPlus as IconProp} />
            Add tier
          </Button>
        </Grid>
      </Grid>
      {freightForwarder &&
        freightForwarder.client_tiers?.map((tier, index) => {
          return (
            <Grid container spacing={3} key={index}>
              <Grid item xs={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Tier Name
                </Typography>
                <RoundedInput
                  value={tier.name}
                  name={`client_tiers.${index}.name`}
                  placeholder="Enter Tier Name"
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={3}>
                <Grid container spacing={3} alignItems="flex-end" className={classes.fullHeight}>
                  <Grid item xs>
                    <Button
                      onClick={() => deleteTier(index)}
                      size="large"
                      variant="contained"
                      color="primary"
                      className={`${classes.roundedButton} ${classes.deleteButton}`}
                    >
                      <FontAwesomeIcon className={classes.leftIcon} icon={faTrash as IconProp} />
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          );
        })}

      <Divider className={classes.divider} />

      <Typography variant="subtitle2" gutterBottom>
        Terms And Conditions
      </Typography>

      {freightForwarder.terms_and_conditions &&
        freightForwarder.terms_and_conditions.map((term, index) => {
          return (
            <Grid container spacing={1} key={term.mode}>
              <Grid item xs={12}>
                <Typography variant="h5" color="primary" gutterBottom>
                  {term.mode}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <RoundedInput
                  multiline
                  rows={4}
                  value={term.text}
                  name={`terms_and_conditions.${index}.text`}
                  placeholder={`Enter Terms and Conditions for ${term.mode}`}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          );
        })}

      <Divider className={classes.divider} />

      <Typography variant="subtitle2" gutterBottom>
        Conditions Of Trade
      </Typography>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <RoundedInput
            multiline
            rows={4}
            value={freightForwarder.conditions_of_trade}
            name="conditions_of_trade"
            placeholder={'Enter Conditions Of Trade'}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>

      <Divider className={classes.divider} />

      <Grid container spacing={3} className={classes.bottomMargin}>
        <Grid item xs={6}>
          {freightForwarder._id && (
            <Button
              onClick={handleDelete}
              size="large"
              variant="contained"
              color="primary"
              className={`${classes.roundedButton} ${classes.deleteButton}`}
            >
              <FontAwesomeIcon className={classes.leftIcon} icon={faTrash as IconProp} />
              Delete
            </Button>
          )}
        </Grid>
        <Grid item xs={6}>
          <Grid container className={classes.marginYZero} spacing={3} justifyContent="flex-end">
            <Button onClick={goBack} size="large" variant="outlined" color="primary" className={classes.roundedButton}>
              Cancel
            </Button>
            <Button onClick={submit} size="large" variant="contained" color="primary" className={classes.roundedButton}>
              <FontAwesomeIcon className={classes.leftIcon} icon={faPlus as IconProp} />
              {props.submitButtonText}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  ) : null;
};

export default FreightForwarderForm;
